import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Breadcrumbs from "@/components/Breadcrumbs";
import JsonLd from "@/components/JsonLd";
import usePageMeta from "@/hooks/use-page-meta";
import { Calendar, User, RefreshCcw, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BLOGS, BRAND } from "@/constants/content";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOGS.find((b) => b.slug === slug);
  const canonical = post ? `https://${BRAND.domain}/blogs/${post.slug}` : "";

  usePageMeta({
    title: post?.metaTitle || post?.title || "Blog — KinFlex",
    description: post?.metaDescription || post?.excerpt || "",
    canonical,
    ogImage: post?.image,
    ogType: "article",
  });

  if (!post) return <Navigate to="/blogs" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      dateModified: post.updated || post.date,
      author: { "@type": "Organization", name: post.author },
      publisher: { "@type": "Organization", name: BRAND.name, url: `https://${BRAND.domain}` },
      mainEntityOfPage: canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (post.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const others = BLOGS.filter((b) => b.slug !== slug).slice(0, 3);
  const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <Layout>
      <JsonLd data={jsonLd} />

      <section className="pt-28 pb-8 md:pt-36 md:pb-12 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative max-w-4xl">
          <Breadcrumbs items={[{ label: "Blogs", to: "/blogs" }, { label: post.title }]} />
          <div className="mt-6 flex items-center gap-3 text-xs">
            {post.tags?.map((t) => (
              <span key={t} className="inline-flex items-center px-2.5 py-1 rounded-full bg-white border border-slate-200 font-medium" style={{ color: '#0A84FF' }}>{t}</span>
            ))}
          </div>
          <h1 className="mt-4 font-display font-bold text-slate-900 text-4xl md:text-5xl tracking-tight leading-[1.1]">{post.title}</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl">{post.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {post.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Published {fmt(post.date)}</span>
            {post.updated && <span className="inline-flex items-center gap-1.5"><RefreshCcw className="h-3.5 w-3.5" /> Updated {fmt(post.updated)}</span>}
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium"><ShieldCheck className="h-3.5 w-3.5" /> Medically reviewed</span>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="container-page grid lg:grid-cols-4 gap-10">
          <div className="lg:col-span-3">
            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 mb-8">
              <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
            </div>

            <article className="prose max-w-none">
              {post.content?.map((c, i) => (
                <div key={i} className="mb-8">
                  <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">{c.h}</h2>
                  <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">{c.p}</p>
                </div>
              ))}
            </article>

            {post.faqs && (
              <div className="mt-10">
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="mt-4 space-y-2.5">
                  {post.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`bfaq-${i}`} className="border border-slate-200 rounded-xl bg-white data-[state=open]:border-[#0A84FF]/40 px-4">
                      <AccordionTrigger className="text-left font-display font-semibold text-slate-900 hover:no-underline py-4">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-4">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            <div className="mt-10 rounded-3xl bg-slate-900 text-white p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: '#4dabff' }}>Ready to book?</div>
              <h3 className="mt-3 font-display font-bold text-2xl leading-tight">Need home healthcare in Delhi NCR?</h3>
              <p className="mt-2 text-sm text-slate-300">Talk to our care coordinator — same-day bookings, transparent pricing.</p>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-5 py-2.5 text-sm font-semibold">
                Book Appointment <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start">
            {post.toc && (
              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 mb-5">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Table of Contents</div>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  {post.toc.map((t, i) => (<li key={i}>· {t}</li>))}
                </ul>
              </div>
            )}
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">More reading</div>
              <ul className="space-y-2">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to={`/blogs/${o.slug}`} className="text-sm font-medium text-slate-700 hover:text-[#0A84FF]">→ {o.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
